import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Check, Star, Sparkles, Gift } from "lucide-react";

const AnimatedBlock = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

const communityFeatures = [
  "Accesso immediato a tutti i contenuti gi\u00e0 presenti",
  "Live mensili di formazione",
  "Cerchi di Luce",
  "Community privata su Telegram",
  "Libreria delle pratiche",
  "Workshop con professionisti",
  "Eventi dal vivo",
  "Networking",
  "Sconti esclusivi sui percorsi individuali",
];

const oltreFeatures = [
  "Tutti i servizi di Light Workers House",
  "10 sessioni individuali da 90 minuti",
  "Supporto personale via Telegram",
  "Community privata",
  "Cerchi di Luce",
  "Live mensili",
  "Libreria completa delle pratiche",
  "Workshop",
  "Eventi",
];

const PricingSection = () => {
  return (
    <section
      data-testid="pricing-section"
      id="pricing-section"
      className="py-24 md:py-32 bg-[#F5F0EB] relative overflow-hidden"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-12 relative">
        <AnimatedBlock>
          <div className="text-center mb-6">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-[#442F23] mb-6">
              Scegli il percorso pi&ugrave; adatto a te
            </h2>
            <div className="section-divider mb-6" />
            <p className="font-heading text-lg md:text-xl text-[#442F23]/80 leading-relaxed italic max-w-2xl mx-auto">
              La domanda ora non &egrave; se questa trasformazione &egrave; possibile.<br />
              La domanda &egrave;: <span className="text-[#AC8E60] font-semibold">quale livello di accompagnamento senti pi&ugrave; adatto al momento che stai vivendo?</span>
            </p>
          </div>
        </AnimatedBlock>

        {/* Featured photo */}
        <AnimatedBlock delay={0.05}>
          <div className="mb-16 max-w-2xl mx-auto">
            <div className="rounded-[2rem] overflow-hidden shadow-[0_20px_60px_rgba(68,47,35,0.08)]">
              <img
                src="https://customer-assets.emergentagent.com/job_inner-light-path-2/artifacts/l57wrryd_Minimalist%20Fashion%20Presentation%20Template%281%29.jpg"
                alt="Alessia Stella - Light Workers House"
                className="w-full h-auto object-contain"
                data-testid="pricing-featured-photo"
              />
            </div>
          </div>
        </AnimatedBlock>

        {/* 3 Pricing Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">

          {/* ANNUALE */}
          <AnimatedBlock delay={0.1}>
            <div className="bg-[#FBF8E9] border border-[#AC8E60]/15 rounded-[2rem] p-8 card-hover h-full flex flex-col" data-testid="pricing-annual">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles size={18} className="text-[#E1C340]" />
                <p className="text-sm uppercase tracking-[0.15em] text-[#AC8E60] font-body font-semibold">
                  Crescita Annuale
                </p>
              </div>
              <h3 className="font-heading text-lg text-[#442F23] font-medium mb-1">
                Light Workers House Annuale
              </h3>
              <p className="font-body text-sm text-[#442F23]/60 mb-6">
                Ideale se desideri fare della tua crescita personale e spirituale uno stile di vita.
              </p>

              {/* 2 modalità di pagamento */}
              <div className="space-y-4 mb-6">
                <div className="bg-[#E1C340]/8 rounded-xl px-4 py-3">
                  <p className="font-body text-xs text-[#AC8E60] font-semibold uppercase mb-1">Pagamento unico</p>
                  <div className="flex items-baseline gap-2">
                    <span className="font-heading text-4xl font-bold text-[#442F23]">&euro;707</span>
                  </div>
                  <p className="font-body text-xs text-[#442F23]/50">meno di 59 &euro;/mese</p>
                </div>
                <div className="bg-[#FBF8E9] border border-[#AC8E60]/15 rounded-xl px-4 py-3">
                  <p className="font-body text-xs text-[#AC8E60] font-semibold uppercase mb-1">Pagamento rateale</p>
                  <div className="flex items-baseline gap-2">
                    <span className="font-heading text-4xl font-bold text-[#442F23]">&euro;65</span>
                    <span className="font-body text-sm text-[#442F23]/50">/mese</span>
                  </div>
                  <p className="font-body text-xs text-[#442F23]/50">780 &euro;/anno</p>
                </div>
              </div>

              <div className="bg-[#E1C340]/10 rounded-xl px-4 py-3 mb-6">
                <p className="font-body text-xs text-[#442F23]/80 flex items-start gap-2">
                  <Gift size={14} className="text-[#E1C340] flex-shrink-0 mt-0.5" />
                  <span><strong>Agosto &egrave; completamente in regalo.</strong> Entrerai subito nella Community con accesso a tutti i contenuti. In pratica vivrai 13 mesi, pagando l'annuale.</span>
                </p>
              </div>

              <p className="font-body text-xs text-[#442F23]/50 mb-1">12 mesi di Membership</p>

              <ul className="space-y-2 mb-8 flex-1">
                {communityFeatures.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check size={14} className="text-[#E1C340] flex-shrink-0 mt-0.5" />
                    <span className="font-body text-xs text-[#442F23]/70">{f}</span>
                  </li>
                ))}
              </ul>

              <div className="space-y-2">
                <a
                  href="https://buy.stripe.com/4gM00dglSgjg4vB7VQ5AQ07"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary block text-center w-full text-sm"
                  data-testid="pricing-annual-cta"
                >
                  Pagamento unico &euro;707
                </a>
                <a
                  href="https://buy.stripe.com/6oUdR33z61ome6bdga5AQ06"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-body font-semibold text-[#AC8E60] border-2 border-[#AC8E60]/30 rounded-full px-6 py-3 hover:border-[#AC8E60] hover:bg-[#AC8E60]/5 transition-all duration-300 block text-center w-full text-sm"
                  data-testid="pricing-annual-rate-cta"
                >
                  Rateale &euro;65/mese
                </a>
              </div>
            </div>
          </AnimatedBlock>

          {/* SEMESTRALE */}
          <AnimatedBlock delay={0.2}>
            <div className="bg-[#FBF8E9] border border-[#AC8E60]/15 rounded-[2rem] p-8 card-hover h-full flex flex-col" data-testid="pricing-semester">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles size={18} className="text-[#E1C340]" />
                <p className="text-sm uppercase tracking-[0.15em] text-[#AC8E60] font-body font-semibold">
                  Crescita Semestrale
                </p>
              </div>
              <h3 className="font-heading text-lg text-[#442F23] font-medium mb-1">
                Light Workers House Semestrale
              </h3>
              <p className="font-body text-sm text-[#442F23]/60 mb-6">
                Ideale se desideri iniziare il tuo cammino con la Community.
              </p>

              <p className="font-body text-xs text-[#442F23]/40 mb-1">Valore reale</p>
              <p className="font-body text-xl text-[#442F23]/40 line-through font-semibold mb-2">&euro;532</p>
              <p className="font-body text-xs text-[#AC8E60] font-semibold uppercase mb-1">Offerta di Lancio</p>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-heading text-5xl font-bold text-[#442F23]">&euro;444</span>
              </div>
              <p className="font-body text-xs text-[#E1C340] font-bold uppercase mb-4">
                Valida fino al 31 luglio
              </p>

              <div className="bg-[#E1C340]/10 rounded-xl px-4 py-3 mb-6">
                <p className="font-body text-xs text-[#442F23]/80 flex items-start gap-2">
                  <Gift size={14} className="text-[#E1C340] flex-shrink-0 mt-0.5" />
                  <span><strong>Agosto &egrave; completamente in regalo.</strong> Le attivit&agrave; del semestre inizieranno a settembre, ma entrerai subito nella Community con accesso a tutti i contenuti. In pratica vivrai 7 mesi, pagando il semestre.</span>
                </p>
              </div>

              <ul className="space-y-2 mb-8 flex-1">
                {communityFeatures.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check size={14} className="text-[#E1C340] flex-shrink-0 mt-0.5" />
                    <span className="font-body text-xs text-[#442F23]/70">{f}</span>
                  </li>
                ))}
              </ul>

              <a
                href="https://buy.stripe.com/7sYfZbc5CaYWbY3cc65AQ05"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary block text-center w-full text-sm"
                data-testid="pricing-semester-cta"
              >
                Inizio il mio cammino
              </a>
            </div>
          </AnimatedBlock>

          {/* OLTRE */}
          <AnimatedBlock delay={0.3}>
            <div className="bg-[#FBF8E9] pricing-featured rounded-[2rem] p-8 card-hover h-full flex flex-col relative" data-testid="pricing-oltre">
              <div className="absolute top-6 right-6">
                <span className="inline-flex items-center gap-1 bg-[#E1C340]/15 text-[#AC8E60] text-[10px] font-body font-semibold px-3 py-1 rounded-full uppercase tracking-wider">
                  <Star size={10} className="fill-[#E1C340] text-[#E1C340]" />
                  Completo
                </span>
              </div>

              <div className="flex items-center gap-2 mb-4">
                <Sparkles size={18} className="text-[#E1C340]" />
                <p className="text-sm uppercase tracking-[0.15em] text-[#AC8E60] font-body font-semibold">
                  OLTRE
                </p>
              </div>
              <h3 className="font-heading text-lg text-[#442F23] font-medium mb-1">
                Light Workers' House + Mentoring 1:1 &mdash; 6 mesi
              </h3>
              <p className="font-body text-sm text-[#442F23]/60 mb-6">
                Accompagnamento personale nella tua trasformazione.
              </p>

              <div className="mb-2">
                <p className="font-body text-xs text-[#AC8E60] font-semibold uppercase mb-3">Il tuo investimento</p>
                <p className="font-body text-sm text-[#442F23]/70 mb-2">Mentoring Privato OLTRE + Membership completa Light Workers House</p>
                <p className="font-body text-xs text-[#442F23]/40 mb-1">Valore <span className="line-through font-semibold">&euro;2.532</span></p>
              </div>
              <p className="font-body text-xs text-[#AC8E60] font-semibold uppercase mb-1">Investimento di lancio</p>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-heading text-5xl font-bold text-[#442F23]">&euro;2.000</span>
              </div>
              <p className="font-body text-xs text-[#E1C340] font-bold uppercase mb-4">
                Valida fino al 31 luglio
              </p>

              <ul className="space-y-3 mb-8 flex-1">
                <li className="flex items-start gap-2">
                  <Sparkles size={14} className="text-[#E1C340] flex-shrink-0 mt-0.5" />
                  <span className="font-body text-xs text-[#442F23]/70">6 mesi di accompagnamento trasformativo per sostenere la tua evoluzione personale e spirituale.</span>
                </li>
                <li className="flex items-start gap-2">
                  <Sparkles size={14} className="text-[#E1C340] flex-shrink-0 mt-0.5" />
                  <span className="font-body text-xs text-[#442F23]/70">Il mio Mentoring Privato &mdash; 10 sessioni individuali da 90 minuti, completamente personalizzate, per sciogliere i blocchi che ancora limitano la tua espansione, alimentare il tuo Fuoco Interiore e accompagnarti a incarnare pienamente la tua missione.</span>
                </li>
                <li className="flex items-start gap-2">
                  <Sparkles size={14} className="text-[#E1C340] flex-shrink-0 mt-0.5" />
                  <span className="font-body text-xs text-[#442F23]/70">Accesso diretto a me via WhatsApp per tutti i 6 mesi, per ricevere guida, chiarezza e supporto tra una sessione e l'altra.</span>
                </li>
                <li className="flex items-start gap-2">
                  <Sparkles size={14} className="text-[#E1C340] flex-shrink-0 mt-0.5" />
                  <span className="font-body text-xs text-[#442F23]/70">Accesso immediato a Light Workers House, con Community privata, Live mensili, Cerchi di Luce e tutti i nuovi contenuti del percorso.</span>
                </li>
                <li className="flex items-start gap-2">
                  <Sparkles size={14} className="text-[#E1C340] flex-shrink-0 mt-0.5" />
                  <span className="font-body text-xs text-[#442F23]/70">Accesso completo alla Libreria delle Pratiche, con meditazioni, pratiche energetiche, lavoro somatico, visualizzazioni e strumenti di integrazione.</span>
                </li>
              </ul>

              <a
                href="https://docs.google.com/forms/d/e/1FAIpQLSfIxVeTBmTpUlxlTjdylRRwZb_5JNIvVdae41KJB0vdB0z1rA/viewform?pli=1"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary block text-center w-full text-sm"
                data-testid="pricing-oltre-cta"
              >
                Scelgo OLTRE e il Mentoring 1:1
              </a>
            </div>
          </AnimatedBlock>
        </div>

        <AnimatedBlock delay={0.4}>
          <p className="text-center font-body text-sm text-[#E1C340] font-bold uppercase">
            Tutte le offerte sono valide fino al 31 luglio 2026
          </p>
        </AnimatedBlock>
      </div>
    </section>
  );
};

export default PricingSection;
